import { defineStore } from 'pinia';

export interface Basic {
    id: string;
    name: string;
    code: string;
    active: boolean;
    alternateName1: string;
    alternateName2: string;
    taxRate: string;
    member: string;
}
export interface TimeControl {
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    daysOfWeek: string[];
}

export interface TriggerData {
    type: string;
}

export interface PLUData {
    id: string;
    name: string;
}

export interface TriggerIndividualDepartment extends TriggerData {
    options: {
        triggerAmountType: string,
        triggerAmountValue: number,
        triggerAmountMode: string,
        applicableDestination: string,
    };
    departmentList: string[];
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TriggerMultiDepartment extends TriggerData {
    options: {
        triggerAmountType: string,
        triggerAmountMode: string,
    };
    firstDepartment: {
        department: string,
        triggerAmountValue: number,
        applicableDestination: string,
    };
    secondDepartment: {
        department: string,
        triggerAmountValue: number,
        applicableDestination: string,
    };
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TriggerIndividualGroup extends TriggerData {
    options: {
        triggerAmountType: string,
        triggerAmountValue: number,
        triggerAmountMode: string,
        applicableDestination: string,
    };
    productGroupList: string[];
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TriggerMultiProductGroup extends TriggerData {
    options: {
        triggerAmountType: string,
        triggerAmountMode: string,
    };
    firstProductGroup: {
        productGroup: string,
        triggerAmountValue: number,
        applicableDestination: string,
    };
    secondProductGroup: {
        productGroup: string,
        triggerAmountValue: number,
        applicableDestination: string,
    };
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TriggerIndividualPLU extends TriggerData {
    options: {
        triggerAmountType: string,
        triggerAmountValue: number,
        triggerAmountMode: string,
        applicableDestination: string,
    };
    PLUList: PLUData[];
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface Trigger {
    selectedTrigger: string;
    triggerData: TriggerData | null;
}

export interface TypeMultiBuy extends typeData {
    basic: {
        discountType: string,
        QTYList: { id: string, qty: number, value: number }[]
    };
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TypeFixedValue extends typeData {
    basic: {
        discountType: string,
        discountPrice: number,
    };
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TypePercentageOff extends typeData {
    basic: {
        percentageOff: number,
    };
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TypeCheapestOneFree extends typeData {
    basic: {
        discountType: string,
        maximumQty: number,
        discountValue: number,
    };
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TypeDiscountAfterN extends typeData {
    basic: {
        discountTresholdQty: number,
        discountType: string,
        maximumQty: number,
        discountValue: number,
    };
    subtotal: {
        include: {
            itemDiscount: boolean,
            addOnTaxes: boolean,
            addOnServiceCharges: boolean,
            condiments: boolean,
        },
        exclude: {
            includedTaxes: boolean,
            includedServiceCharges: boolean,
        },
    };
}

export interface TypeBuyXGetYFree extends typeData {
    group: {
        x: number,
        y: number,
        groupItemsBy: string,
        OnlyReserveDiscountedItems: boolean,
        ExcludeFreeItems: boolean,
    };
    priceComputation: {
        includeCondiments: boolean,
        includeItemDiscount: boolean,
        includeItemSurCharge: boolean,
        includeAddOnServiceCharges: boolean,
    };
    discountComputation: {
        roundingFactor: number,
        roundingPolicy: string,
        roundAvgPriceIndividually: boolean,
        discountOncePerGroup: boolean,
    };
}

export interface TypeAmountOff extends typeData {
    basic: {
        discountType: string,
        amountOffValue: number,
    }
}

export interface typeData {
    type: string;
}

export interface Type {
    selectedType: string;
    typeData: typeData | null;
}

export interface Promotion {
    basic: Basic;
    timeControl: TimeControl;
    trigger: Trigger;
    type: Type;
}

export const promotionsMock = [
    {
        basic: {
            id: '1',
            name: 'Promotion 1',
            code: 'PROMO1',
            active: true,
            alternateName1: 'Promotion 1',
            alternateName2: 'Promotion 1',
            taxRate: 'Tax',
            member: '0',
        },
        timeControl: {
            startDate: '2021-01-01',
            endDate: '2021-12-31',
            startTime: '00:00',
            endTime: '23:59',
            daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        },
        trigger: {
            selectedTrigger: '3',
            triggerData: null
        },
        type: {
            selectedType: '2',
        }
    },
    {
        basic: {
            id: '2',
            name: 'Promotion 2',
            code: 'PROMO2',
            active: true,
            alternateName1: 'Promotion 2',
            alternateName2: 'Promotion 2',
            taxRate: 'Tax2',
            member: '0',
        },
        timeControl: {
            startDate: '2021-01-01',
            endDate: '2021-12-31',
            startTime: '00:00',
            endTime: '23:59',
            daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        },
        trigger: {
            selectedTrigger: '3',
            triggerData: null
        },
        type: {
            selectedType: '2',
        }
    }] as Promotion[];

export const defaultPromotion = {
    basic: {
        id: '',
        name: '',
        code: '',
        active: true,
        alternateName1: '',
        alternateName2: '',
        taxRate: '',
        member: '',
    },
    timeControl: {
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',
        daysOfWeek: [],
    },
    trigger: {
        selectedTrigger: '3',
        triggerData: null,
    },
    type: {
        selectedType: '2',
    }
};

export const triggerList = [
    {
        id: '1',
        name: 'Individual Department',
        description: 'TBD',
    },
    {
        id: '2',
        name: 'Multi Department',
        description: 'TBD',
    },
    {
        id: '3',
        name: 'ByPass',
        description: 'ByPass Trigger Always pass success to promotion type',
    },
    {
        id: '4',
        name: 'Individual Group',
        description: 'TBD',
    },
    {
        id: '5',
        name: 'Multi Product Group',
        description: 'TBD',
    },
    {
        id: '6',
        name: 'Individual PLU',
        description: 'TBD',
    }
];

export const typeList = [
    {
        id: '1',
        name: 'Multi Buy',
        description: 'TBD',
    },
    {
        id: '2',
        name: 'ByPass',
        description: 'ByPass type always pass success',
    },
    {
        id: '3',
        name: 'Fixed Value',
        description: 'TBD',
    },
    {
        id: '4',
        name: 'Percentage Off',
        description: 'TBD',
    },
    {
        id: '5',
        name: 'Cheapest One Free',
        description: 'TBD',
    },
    {
        id: '6',
        name: 'Discount After N',
        description: 'Promotion type - Discount After N: no discount is applied to the first N items from matched item set',
    },
    {
        id: '7',
        name: 'Buy X Get Y Free (by average Price)',
        description: 'Promotion type - Buy x Get y Free (by Average Price): discount is computed by taking the overall average price of matching items then multiply that average by the number of eligible free items',
    },
    {
        id: '8',
        name: 'Amount Off',
        description: 'TBD',
    }
];

export const roundingPolicyList = [
    {
        id: '1',
        name: 'None',
    },
    {
        id: '2',
        name: 'round to nearest factor',
    },
    {
        id: '3',
        name: 'round down to factor',
    },
    {
        id: '4',
        name: 'round up to factor',
    },
    {
        id: '5',
        name: 'round to 5 cents',
    },
    {
        id: '6',
        name: 'round to 5 cents(2)',
    },
    {
        id: '7',
        name: 'round to 10 cents(1)',
    },
    {
        id: '8',
        name: 'round to 10 cents(2)',
    },
    {
        id: '9',
        name: 'round to 25 cents',
    },
    {
        id: '10',
        name: 'round to 50 cents',
    },
    {
        id: '11',
        name: 'round to 50 cents(2)',
    }
]

export const usePromotionStore = defineStore('promotions', {
    state: () => ({
        promotionList: [] as Promotion[],
        activePromotion: {} as Promotion,
    }),
    getters: {
        getActivePromotion: (state) => {
            return state.activePromotion;
        },
        findPromotionById: (state) => (id: string) => {
            return state.promotionList.find(promotion => promotion.basic.id === id);
        },
        getPromotionList: (state) => {
            return state.promotionList;
        }
    },
    actions: {
        initializePromotions(promotions: Promotion[]) {
            this.promotionList = promotions;
        },
        addPromotion(promotion: Promotion) {
            this.promotionList.push(promotion);
        },
        removePromotion(id: string) {
            this.promotionList = this.promotionList.filter(promotion => promotion.basic.id !== id);
        },
        updatePromotion(updatedPromotion: Promotion) {
            const index = this.promotionList.findIndex(promotion => promotion.basic.id === updatedPromotion.basic.id);
            if (index !== -1) {
                this.promotionList[index] = updatedPromotion;
            }
        },
        setActivePromotion(promotion: Promotion) {
            this.activePromotion = promotion;
        },
        setTriggerData(triggerData: TriggerIndividualDepartment) {
            this.activePromotion.trigger.triggerData = triggerData;
        }
    },
});